import React, { useState, useEffect, useRef } from 'react';
import { ModalWrapper, ButtonGroup, TableWrapper } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Row, Col, Popconfirm, Table, TimePicker } from 'antd';
import { useB2cScheduleDetail, useB2cSchedule } from 'hooks';
import { LectureTime, Lecturer, LecturePlace, B2cDailyOperator, LectureSubject, PmCm, Evaluation, NotionUrl, DatePicker } from 'components/atoms'
import { getCodeNumberArray } from 'utils/form-utils';
import moment, { Moment } from 'moment';
import CheckDuplicateModal from 'components/molecules/CheckDuplicateModal';
import { fetcher } from 'services';
import { dateFormat } from 'utils/date-utils';
import { ColumnsType } from 'antd/lib/table/interface';

export interface SeminarScheduleEditModalProps extends PopupProps {
    group_id: number;
    id: number;
}

const SeminarScheduleEditModal = ({ 
    isOpen, 
    onClose, 
    id, group_id}: SeminarScheduleEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const [isCheckConfirm, setIsCheckConfirm] = useState<boolean>(false);   
    const [checkMessage, setCheckMessage] = useState<string>('');     
    const [scheduleSrl, setScheduleSrl] = useState<number>(id);
    const modalRef = useRef<null | HTMLDivElement>(null);
    const [startTime, setStartTime] = useState<string>('09:00');
    const [endTime, setEndTime] = useState<string>('10:00');    

  /* ======================================================
  * hooks
  * ====================================================== */
  const { lastInsertSrl, scheduleInfo, insertSuccess, updateSuccess, deleteSuccess, b2cScheduleInsert, b2cScheduleUpdate, b2cScheduleDelete } = useB2cScheduleDetail(scheduleSrl, group_id);
  const { scheduleList, refetch } = useB2cSchedule(group_id);

   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { TextArea } = Input;

  const columns: ColumnsType<ProjectScheduleInfoDto> = [
    {
      title: '강사',
      dataIndex: 'lecturer',
      key: 'lecturer',
      width: '100px',
      align: 'center',
      ellipsis: true,
    },
    {
     title: '강의일자',
     dataIndex: 'schedule_date',
     key: 'schedule_date',
     width: 'auto',
     align: 'center',
     ellipsis: true,
     render: (schedule_date, record) => {
        return (
            (scheduleSrl === record.id)?(
                <span>{schedule_date}&nbsp;{record.start_time}&nbsp;~&nbsp;{record.end_time}&nbsp;({record.time_count}시간)</span>
            ):(
                <a onClick={()=>{
                    setScheduleSrl(record.id);         
                    ScrollToTop();
                }}>{schedule_date}&nbsp;{record.start_time}&nbsp;~&nbsp;{record.end_time}&nbsp;({record.time_count}시간)</a>
            )
            
        )
      }      
   },    
  ];    
   /* ======================================================
   * events
   * ====================================================== */
   function ScrollToTop() {    
    
    modalRef.current?.scrollIntoView(true);
  }

    const onCancel = () => {
        onClose?.(insertSuccess || updateSuccess || deleteSuccess);
    };

    const onCopy = () => {
        if (!!scheduleInfo) {
            const sData = {
                id: 0,
                b2c_group_srl: scheduleInfo.b2c_group_srl,
                notion_url: form.getFieldValue('notion_url'),
                lecture_place_type: form.getFieldValue('lecture_place_type'),
                lecture_studio: form.getFieldValue('lecture_studio'),
                location: form.getFieldValue('location'),            
                memo: form.getFieldValue('memo'),
                lecturer_srl: form.getFieldValue('lecturer_srl'),
                schedule_date: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD'),
                start_time: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + startTime,
                end_time: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + endTime,                 
                time_count: form.getFieldValue('time_count'),
                lecture_subject1: form.getFieldValue('lecture_subject1'),
                lecture_subject_memo: form.getFieldValue('lecture_subject_memo'),
                pm: form.getFieldValue('pm') ? form.getFieldValue('pm').map((code) => code).join(',') : '',
                cm: form.getFieldValue('cm') ? form.getFieldValue('cm').map((code) => code).join(',') : '',
                need_operater_yn: form.getFieldValue('need_operater_yn')?'Y':'N',
                need_operater_num: form.getFieldValue('need_operater_num'),
                class_evaluation: form.getFieldValue('class_evaluation'),
                class_evaluation_max: form.getFieldValue('class_evaluation_max'),
                nps_evaluation: form.getFieldValue('nps_evaluation'),
                is_no_evaluation: form.getFieldValue('is_no_evaluation') ? 'Y' : 'N',
            }

            if (sData.start_time && sData.end_time && (sData.lecturer_srl || (sData.lecture_place_type === 'ONLINE' && sData.lecture_studio && sData.lecture_studio !== 'ETC'))) {
                // 중복 검사
                fetcher.b2c.postProjectScheduleCheck({
                    id: 0,
                    lecture_studio: sData.lecture_studio,
                    lecturer_srl: sData.lecturer_srl,
                    start_time: sData.start_time,
                    end_time: sData.end_time
                })
                .then((res) => {
                    switch (res.code) {
                        case 200:
                            b2cScheduleInsert(sData)
                        break;
                        default:  
                            setCheckMessage(res.message);
                            setIsCheckConfirm(true);
                        break;
                    };
                });                 
            } else {
                b2cScheduleInsert(sData)
            }
        }
    }

    const onSubmit = (e) => {
        if (!!scheduleInfo) {
            if (!!e.class_evaluation && (e.class_evaluation < 0 || e.class_evaluation_max < e.class_evaluation)) {
                alert("강의 평점 입력이 잘못되었습니다.");
                return;
            } 
                        
            const sData = {
                id: scheduleSrl,
                b2c_group_srl: scheduleInfo.b2c_group_srl,
                notion_url: e.notion_url,
                lecture_place_type: e.lecture_place_type,
                lecture_studio: e.lecture_studio,
                location: e.location,
                memo: e.memo,
                lecturer_srl: e.lecturer_srl,
                schedule_date: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD'),
                start_time: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + startTime,
                end_time: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + endTime, 
                time_count: e.time_count,
                lecture_subject1: e.lecture_subject1,
                lecture_subject_memo: e.lecture_subject_memo,
                pm: e.pm ? e.pm.map((code) => code).join(',') : '',
                cm: e.pm ? e.cm.map((code) => code).join(',') : '',
                need_operater_yn: e.need_operater_yn ? 'Y' : 'N',
                need_operater_num: e.need_operater_num,
                class_evaluation: e.class_evaluation,
                class_evaluation_max: e.class_evaluation_max,
                nps_evaluation: e.nps_evaluation,
                is_no_evaluation: e.is_no_evaluation ? 'Y' : 'N',
            }

            if (sData.start_time && sData.end_time && (sData.lecturer_srl || (sData.lecture_place_type === 'ONLINE' && sData.lecture_studio && sData.lecture_studio !== 'ETC'))) {
                // 중복 검사
                fetcher.b2c.postProjectScheduleCheck({
                    id: scheduleSrl,
                    lecture_studio: sData.lecture_studio,
                    lecturer_srl: sData.lecturer_srl,
                    start_time: sData.start_time,
                    end_time: sData.end_time
                })
                .then((res) => {
                    switch (res.code) {
                        case 200:
                            if (scheduleSrl > 0) {
                                b2cScheduleUpdate(sData)
                            } else {
                                b2cScheduleInsert(sData)
                            }                                
                        break;
                        default:  
                            setCheckMessage(res.message);
                            setIsCheckConfirm(true);
                        break;
                    };
                });                 
            } else {
                if (scheduleSrl > 0) {
                    b2cScheduleUpdate(sData)
                } else {
                    b2cScheduleInsert(sData)
                }  
            }
        }
 
    };

    useEffect(() => {
        if (insertSuccess || updateSuccess ||deleteSuccess) {
            refetch();
        }
        if (!!insertSuccess && lastInsertSrl > 0) setScheduleSrl(lastInsertSrl);

    }, [insertSuccess, updateSuccess, deleteSuccess])

  /* ======================================================
   * effects
   * ====================================================== */
    useEffect(() => {
        if (!!scheduleInfo) {
            form.setFieldsValue({
                notion_url: scheduleInfo.notion_url,
                lecture_place_type: scheduleInfo.lecture_place_type,
                lecture_studio: scheduleInfo.lecture_studio,
                location: scheduleInfo.location,
                memo: scheduleInfo.memo,
                lecturer_srl: scheduleInfo.lecturer_srl,
                time_count: scheduleInfo.time_count,
                lecture_subject1: scheduleInfo.lecture_subject1,
                lecture_subject_memo: scheduleInfo.lecture_subject_memo,
                pm: getCodeNumberArray(scheduleInfo.pm),
                cm: getCodeNumberArray(scheduleInfo.cm),
                need_operater_yn: scheduleInfo.need_operater_yn==='Y',
                need_operater_num: scheduleInfo.need_operater_num,
                class_evaluation: scheduleInfo.class_evaluation,
                class_evaluation_max: scheduleInfo.class_evaluation_max || 5,
                nps_evaluation: scheduleInfo.nps_evaluation,
                is_no_evaluation: scheduleInfo.is_no_evaluation === 'Y',
                schedule_date: (!!scheduleInfo.schedule_date && scheduleInfo.schedule_date !== '0000-00-00') ? moment(scheduleInfo.schedule_date, "YYYY-MM-DD") : undefined,
            });

            setStartTime(scheduleInfo.start_time?scheduleInfo.start_time:"09:00");
            setEndTime(scheduleInfo.end_time?scheduleInfo.end_time:"10:00");
        }
    }, [scheduleInfo])

    return (
        <>
            <ModalWrapper open={isOpen} onCancel={onCancel} width={750} title={scheduleSrl===0?'세미나 스케줄 등록':'세미나 스케줄 수정'} closable>
                <div style={{width:"100%"}} ref={modalRef}>
                <Form
                    form={form}
                    name="b2cScheduleInfo"
                    layout="horizontal"  
                    labelCol={{ flex: '120px' }}
                    onFinish={onSubmit}
                    style={{width:'100%'}}
                    autoComplete="off"
                >
                    <Row>
                        <Col span={12}>
                        <Form.Item label={'세미나명'}>
                            <label>{scheduleInfo?.group_name}</label>                  
                        </Form.Item> 
                        </Col> 
                        <Col span={12}> 
                        <Form.Item label={'담당자'}>
                            <label>{scheduleInfo?.crm_name}</label>                  
                        </Form.Item>   
                        </Col>            
                    </Row>
                    <Form.Item label={'강의요청서'}>                    
                        <NotionUrl form={form} url={(scheduleSrl > 0 && !!scheduleInfo?.notion_url ? scheduleInfo?.notion_url : scheduleInfo?.group_notion_url) || ''} />   
                    </Form.Item> 
                    <Form.Item name="lecturer_srl" label={'강사'} rules={[{ required: true, message: '' }]}>
                        <Lecturer form={form} required={true}/>
                    </Form.Item>                       
                    <Form.Item label={'강의 주제'}>
                        <LectureSubject form={form} />
                    </Form.Item>   
                    <Form.Item 
                            name="schedule_date" 
                            label={'강의 일자'}
                            rules={[{ required: true, message: '' }]}
                        >
                            <DatePicker 
                                showTime={false} 
                                format="YYYY-MM-DD" 
                                disabledDate={(d) => d.isBefore(scheduleInfo?.start_date) || d.isAfter(scheduleInfo?.end_date)}
                            />                    
                        </Form.Item>                                                                          
                    <Form.Item name="lecture_time" label={'강의 시간'}>
                        <Form.Item style={{ display: 'inline-block', marginRight: '5px'}}>
                        <TimePicker
                            format={'HH:mm'} 
                            minuteStep={5} 
                            showNow={false}
                            value={moment(startTime, "HH:mm")}
                            popupClassName={"noFooterTimePick"}
                            onSelect={(value) => {
                                const timeString = moment(value).format("HH:mm");
                                setStartTime(timeString);
                            }}
                        />  
                        <label>&nbsp;~&nbsp;</label>
                        <TimePicker
                            format={'HH:mm'} 
                            minuteStep={5} 
                            showNow={false}
                            value={moment(endTime, "HH:mm")}
                            popupClassName={"noFooterTimePick"}
                            onSelect={(value) => {
                                const timeString = moment(value).format("HH:mm");
                                setEndTime(timeString);
                            }}
                        />   
                        </Form.Item>
                        <Form.Item name="time_count" style={{ width:'50px', display: 'inline-block', marginRight: '5px' }}>
                            <Input type="number" placeholder='시간'/>  
                        </Form.Item>   
                        <Form.Item style={{ display: 'inline-block'}}>
                        <label>시간</label>                                           
                        </Form.Item>
                    </Form.Item>                        
                    <Form.Item label={'교육 장소'}>
                        <LecturePlace required={false} form={form} lecture_place_type={scheduleInfo?.lecture_place_type || 'OFFLINE'} lecture_studio={scheduleInfo?.lecture_studio || ''} />
                    </Form.Item> 
                    <Form.Item className='form_pmcm' label={'PM/CM/운영'}>
                        <PmCm form={form} pm={scheduleInfo?.pm} cm={scheduleInfo?.cm} />
                        <div>
                            <Form.Item style={{ display: 'inline-block'}}>
                            <label>운영 :&nbsp; </label>
                            </Form.Item>         
                            <Form.Item style={{ display: 'inline-block'}}>
                            <B2cDailyOperator form={form} group_srl={group_id} operator={scheduleInfo?.operator} schedule_date={scheduleInfo?.schedule_date} />
                            </Form.Item>  
                        </div>                         
                    </Form.Item>                     
                    <Form.Item label={'강의평점/NPS'}>
                        <Evaluation form={form} />
                    </Form.Item>                      
                    <Form.Item name='memo' label={'일정 메모'}>
                        <TextArea rows={3} placeholder='일정 메모를 입력하세요'/>   
                    </Form.Item>    
                    <ButtonGroup className={'buttonsWrapper'}>
                        <div>
                            <Button onClick={onCancel} style={{marginRight: "5px"}}>닫기</Button>
                            {(scheduleSrl>0) && (
                                <Popconfirm
                                    title="시간 수정 하였습니까?"
                                    okText="복사"
                                    cancelText="취소"
                                    onConfirm={() => { onCopy() }}
                                >   
                                    <Button>복사</Button>
                                </Popconfirm>                       
                            )}                            
                        </div>   
                        <div>
                            {(scheduleSrl>0 && !!scheduleInfo && (scheduleInfo.out_lecturer_price_yn === 'N')) && (
                                <Popconfirm
                                    title="삭제 하시겠습니까?"
                                    okText="삭제"
                                    cancelText="취소"
                                    onConfirm={() => { b2cScheduleDelete({id:scheduleSrl})}}
                                >   
                                    <Button danger style={{marginRight: "5px"}}>삭제</Button>        
                                </Popconfirm>
                            )}
                            <Button htmlType="submit"  type="primary">{scheduleSrl===0?'등록':'수정'}</Button>
                        </div>                    
                    </ButtonGroup>   
                </Form>
                <TableWrapper isSearch={false}>
                    <Table
                        tableLayout="fixed"
                        dataSource={scheduleList}
                        pagination={false}
                        columns={columns}
                        style={{ width: '100%', overflowX: 'auto',marginTop:'15px'}}
                        showSorterTooltip={false}
                        scroll={{ y: 240 }}
                    />
                </TableWrapper>                
                </div>                
            </ModalWrapper>  
            <CheckDuplicateModal
                title="스케줄 확인"
                message={checkMessage}
                visible={isCheckConfirm}
                onOk={() => setIsCheckConfirm(false)}
            />          
        </>
    )    
};

export default React.memo(SeminarScheduleEditModal);    